import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface CalendarProps {
    selectedDate: Date | null;
    onDateSelect: (date: Date) => void;
    events: { [key: string]: any[] };
    activeStartDate: Date;
    searchKeyword?: string;
}

const Calendar: React.FC<CalendarProps> = ({
    selectedDate,
    onDateSelect,
    events,
    activeStartDate,
    searchKeyword = "",
}) => {
    const [currentMonth, setCurrentMonth] = useState(activeStartDate);
    
    useEffect(() => {
        setCurrentMonth(activeStartDate);
    }, [activeStartDate]);

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const renderCalendarDays = () => {
        const days = [];
        const daysInMonth = getDaysInMonth(currentMonth);
        const firstDay = getFirstDayOfMonth(currentMonth);

        // 이전 달의 마지막 날짜들
        const prevMonthDays = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();
        for (let i = firstDay - 1; i >= 0; i--) {
            days.push(
                <DayCell key={`prev-${i}`} isOtherMonth>
                    {prevMonthDays - i}
                </DayCell>
            );
        }

        // 현재 달의 날짜들
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateStr = date.toISOString().split('T')[0];
            const hasEvents = events[dateStr] && events[dateStr].length > 0;
            const isSelected = selectedDate?.toISOString().split('T')[0] === dateStr;
            
            // 검색어가 있을 때 해당 날짜에 검색된 과제가 있는지 확인
            const hasSearchResults = searchKeyword && hasEvents && 
                events[dateStr].some(event => 
                    event.title.toLowerCase().includes(searchKeyword.toLowerCase())
                );

            days.push(
                <DayCell
                    key={day}
                    onClick={() => onDateSelect(date)}
                    isSelected={isSelected}
                    hasEvents={hasEvents}
                    hasSearchResults={!!hasSearchResults}
                >
                    {day}
                    {hasEvents && (
                        <EventDotsContainer>
                            {events[dateStr].slice(0, 3).map((_, index) => (
                                <EventDot 
                                    key={index} 
                                    hasSearchResults={!!hasSearchResults}
                                />
                            ))}
                            {events[dateStr].length > 3 && (
                                <EventDot 
                                    hasSearchResults={!!hasSearchResults}
                                    isMore={true}
                                />
                            )}
                        </EventDotsContainer>
                    )}
                </DayCell>
            );
        }

        // 다음 달의 시작 날짜들
        const remainingCells = 42 - days.length; // 6주 x 7일 = 42
        for (let i = 1; i <= remainingCells; i++) {
            days.push(
                <DayCell key={`next-${i}`} isOtherMonth>
                    {i}
                </DayCell>
            );
        }

        return days;
    };

    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    return (
        <CalendarContainer>
            <CalendarHeader>
                <MonthDisplay>
                    {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
                </MonthDisplay>
                {searchKeyword && (
                    <SearchIndicator>
                        검색: "{searchKeyword}"
                    </SearchIndicator>
                )}
            </CalendarHeader>
            <WeekDaysContainer>
                {weekDays.map(day => (
                    <WeekDay key={day}>{day}</WeekDay>
                ))}
            </WeekDaysContainer>
            <DaysContainer>
                {renderCalendarDays()}
            </DaysContainer>
            <LegendContainer>
                <LegendItem>
                    <LegendDot />
                    <span>과제 있음</span>
                </LegendItem>
                {searchKeyword && (
                    <LegendItem>
                        <LegendDot isSearchResult />
                    <span>검색 결과</span>
                </LegendItem>
                )}
            </LegendContainer>
        </CalendarContainer>
    );
};

const CalendarContainer = styled.div`
    background: #FFFFFF;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const MonthDisplay = styled.h2`
    margin: 0;
    font-size: 24px;
    color: #1E293B;
    font-weight: 600;
`;

const SearchIndicator = styled.div`
    font-size: 14px;
    color: #DF0000;
    background: #FEF2F2;
    padding: 4px 12px;
    border-radius: 16px;
    font-weight: 500;
`;

const WeekDaysContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 10px;
`;

const WeekDay = styled.div`
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #64748B;
    padding: 15px;
`;

const DaysContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    flex-grow: 1;
`;

const DayCell = styled.div<{ 
    isSelected?: boolean; 
    isOtherMonth?: boolean; 
    hasEvents?: boolean;
    hasSearchResults?: boolean;
}>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60px;
    font-size: 16px;
    cursor: pointer;
    border-radius: 8px;
    color: ${props => props.isOtherMonth ? '#94A3B8' : '#1E293B'};
    background: ${props => {
        if (props.isSelected) return '#FEF2F2';
        if (props.hasSearchResults) return '#FEF3C7';
        return 'transparent';
    }};
    font-weight: ${props => props.isSelected ? '600' : '400'};
    transition: all 0.2s ease;

    &:hover {
        background: ${props => {
            if (props.isSelected) return '#FEF2F2';
            if (props.hasSearchResults) return '#FDE68A';
            return '#F8FAFC';
        }};
        transform: scale(1.05);
    }
`;

const EventDotsContainer = styled.div`
    position: absolute;
    bottom: 8px;
    display: flex;
    gap: 2px;
`;

const EventDot = styled.div<{ hasSearchResults?: boolean; isMore?: boolean }>`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${props => {
        if (props.isMore) return '#94A3B8';
        return props.hasSearchResults ? '#F59E0B' : '#DF0000';
    }};
    opacity: ${props => props.isMore ? 0.6 : 1};
`;

const LegendContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #E2E8F0;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #64748B;
`;

const LegendDot = styled.div<{ isSearchResult?: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.isSearchResult ? '#F59E0B' : '#DF0000'};
`;

export default Calendar; 